import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import styles from './default-animation-option.module.scss';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';
import {Button, Size, Type} from '@Components/button';

export interface DefaultAnimationOptionProps {
  key: string;
  onAnimationTypeClick: () => void;
  introAnimation: AnimationType;
  outroAnimation: AnimationType;
  showAnimateLabelOnly?: boolean;
}

export function DefaultAnimationOption({...props}: DefaultAnimationOptionProps): ReactElement | null {
  const getAnimationName = (animation: AnimationType): string => {
    switch (animation) {
      case AnimationType.BLOCK:
        return window.i18next.t('pmwjs_block');
      case AnimationType.BOUNCE:
        return window.i18next.t('pmwjs_bounce');
      case AnimationType.FADE:
        return window.i18next.t('pmwjs_fade');
      case AnimationType.JELLO:
        return window.i18next.t('pmwjs_jello');
      case AnimationType.PAN:
        return window.i18next.t('pmwjs_pan');
      case AnimationType.POP:
        return window.i18next.t('pmwjs_pop');
      case AnimationType.ROTATE:
        return window.i18next.t('pmwjs_rotate');
      case AnimationType.SCALE:
        return window.i18next.t('pmwjs_scale');
      case AnimationType.SLIDE:
        return window.i18next.t('pmwjs_slide');
      case AnimationType.TUMBLE:
        return window.i18next.t('pmwjs_tumble');
      case AnimationType.WIPE:
        return window.i18next.t('pmwjs_wipe');
      case AnimationType.PEEK:
        return window.i18next.t('pmwjs_peek');
      case AnimationType.ZOOM:
        return window.i18next.t('pmwjs_zoom');
      default:
        return window.i18next.t('pmwjs_none');
    }
  };

  const getButtonText = (): string => {
    const hasIntro = props.introAnimation !== AnimationType.NONE;
    const hasOutro = props.outroAnimation !== AnimationType.NONE;

    if (!hasIntro && !hasOutro) {
      return window.i18next.t('pmwjs_animate');
    }

    if (hasIntro && !hasOutro) {
      return window.i18next.t('pmwjs_animation_entrance_label', {name: getAnimationName(props.introAnimation)});
    }

    if (!hasIntro && hasOutro) {
      return window.i18next.t('pmwjs_animation_exit_label', {name: getAnimationName(props.outroAnimation)});
    }

    if (props.introAnimation === props.outroAnimation) {
      return window.i18next.t('pmwjs_animation_both_same_label', {name: getAnimationName(props.introAnimation)});
    }

    return window.i18next.t('pmwjs_animation_both_different_label', {
      intro: getAnimationName(props.introAnimation),
      outro: getAnimationName(props.outroAnimation),
    });
  };

  return (
    <li className="spacing-p-0 flex flex-h-row flex-items-center justify-content-between" key={props.key}>
      <Text val={window.i18next.t('pmwjs_animation')} size={TextSize.XSMALL} className="content-sub-text" />
      <div className={`flexbox ${styles.container}`}>
        <div className={`flex-column-align-center _fit-height`}>
          <Button
            isSelected={props.showAnimateLabelOnly ? false : props.introAnimation !== AnimationType.NONE || props.outroAnimation !== AnimationType.NONE}
            size={Size.SMALL}
            type={Type.SECONDARY}
            text={props.showAnimateLabelOnly ? window.i18next.t('pmwjs_animate') : getButtonText()}
            textClasses={styles.text}
            icon="icon-animation"
            onClick={(): void => {
              props.onAnimationTypeClick();
            }}
          />
        </div>
      </div>
    </li>
  );
}
